import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Drawer, Box, Stack, Typography, IconButton, Tooltip,
  CircularProgress, InputBase,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import { FiRefreshCw, FiInbox, FiArrowRight, FiSearch, FiX } from "react-icons/fi";
import { MdOutlinePendingActions } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { getPendingAcknowledgments } from "../../../services/todoTask.service";
import { showErrorToast as ToastError } from "../../../utils/Toaster/AppToast";
import { PRI_LABEL, PRI_DOT } from "../constants/todoTokens";
import moment from "moment";

const T = {
  primary:     "#0D9488",
  primaryDark: "#0F766E",
  primaryLight:"#F0FDFA",
  border:      "#E2E8F0",
  text:        "#0F172A",
  textSub:     "#475569",
  textMuted:   "#94A3B8",
  surface:     "#FFFFFF",
  bg:          "#F8FAFC",
};

const TaskCard = ({ task, onNavigate, isAdmin }) => {
  const isOverdue = task.actualDeadlineDate && moment(task.actualDeadlineDate).isBefore(moment(), "day");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.18 }}
    >
      <Box
        onClick={() => onNavigate(task._id)}
        sx={{
          p: 1.8, borderRadius: "12px",
          border: `1px solid ${T.border}`,
          bgcolor: T.surface,
          cursor: "pointer",
          boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
          transition: "all 0.15s ease",
          "&:hover": {
            borderColor: T.primary,
            boxShadow: "0 4px 14px rgba(13,148,136,0.12)",
            transform: "translateY(-1px)",
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.textMuted, mb: 0.3, letterSpacing: "0.04em" }}>
              {task.taskNo}
            </Typography>
            <Typography
              sx={{ fontSize: "13px", fontWeight: 700, color: T.text, lineHeight: 1.4,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={task.title}
            >
              {task.title}
            </Typography>
          </Box>

          {task.priority && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 0.5,
              px: 1, py: 0.4, borderRadius: "6px", flexShrink: 0,
              bgcolor: task.priority === 3 ? "#FEF2F2" : task.priority === 2 ? "#FFFBEB" : "#ECFDF5",
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: PRI_DOT[task.priority] }} />
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: PRI_DOT[task.priority] }}>
                {PRI_LABEL[task.priority]}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Meta */}
        <Stack direction="row" spacing={1.5} sx={{ mt: 0.8, flexWrap: "wrap" }}>
          {task.assigneeName && (
            <Typography sx={{ fontSize: "11px", color: T.textSub, fontWeight: 500 }}>
              👤 {task.assigneeName}
            </Typography>
          )}
          {task.actualDeadlineDate && (
            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: isOverdue ? "#EF4444" : T.textSub }}>
              📅 {moment(task.actualDeadlineDate).format("DD MMM YYYY")}
              {isOverdue && " · Overdue"}
            </Typography>
          )}
        </Stack>

        {/* Acknowledger row — admin only */}
        {isAdmin && task.acknowledgerName && (
          <Box sx={{ mt: 0.8, px: 1, py: 0.5, borderRadius: "6px", bgcolor: "#F0FDFA", border: `1px solid ${T.primary}22` }}>
            <Typography sx={{ fontSize: "11px", color: T.primary, fontWeight: 600 }}>
              Pending acknowledgment from: {task.acknowledgerName}
            </Typography>
          </Box>
        )}

        {/* Review CTA */}
        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mt: 1.2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}
            sx={{ fontSize: "11px", fontWeight: 700, color: T.primary }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.primary }}>
              Review &amp; Acknowledge
            </Typography>
            <FiArrowRight size={12} color={T.primary} />
          </Stack>
        </Stack>
      </Box>
    </motion.div>
  );
};

const PendingAcknowledgmentList = ({ open, onClose, onCountChange, isAdmin = false }) => {
  const navigate = useNavigate();
  const [tasks, setTasks]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);

  const fetchTasks = useCallback(async (searchVal) => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 50 };
      if (searchVal) params.search = searchVal;
      const res = await getPendingAcknowledgments(params);
      if (res?.error === false) {
        setTasks(res.data || []);
        setTotal(res.total || 0);
        if (!searchVal) onCountChange?.(res.total || 0);
      }
    } catch {
      ToastError("Failed to load pending acknowledgments");
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    if (open) { setSearch(""); setDebouncedSearch(""); fetchTasks(""); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    fetchTasks(debouncedSearch);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 400);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
  }, []);

  const handleNavigate = useCallback((taskId) => {
    onClose();
    navigate(`/taskDetails?taskId=${taskId}`);
  }, [navigate, onClose]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 400 },
          borderRadius: "16px 0 0 16px",
          boxShadow: "-8px 0 40px rgba(15,23,42,0.12)",
          border: "none",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        background: `linear-gradient(135deg, ${T.primaryLight} 0%, #fff 100%)`,
        borderBottom: `1px solid ${T.border}`,
        flexShrink: 0,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={{
              width: 34, height: 34, borderRadius: "10px",
              background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 10px rgba(13,148,136,0.28)",
            }}>
              <MdOutlinePendingActions size={18} color="#fff" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "15px", color: T.text }}>
                Acknowledgment Queue
              </Typography>
              <Typography sx={{ fontSize: "11px", color: T.textMuted, fontWeight: 500 }}>
                {total} task{total !== 1 ? "s" : ""} {isAdmin ? "pending acknowledgment" : "awaiting your review"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => fetchTasks(debouncedSearch)} disabled={loading} sx={{ color: T.textMuted }}>
                <FiRefreshCw size={14} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose} sx={{ color: T.textSub }}>
              <IoClose size={18} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Search bar */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${T.border}`, flexShrink: 0, bgcolor: "#fff" }}>
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{
            px: 1.4, py: 0.8, borderRadius: "10px",
            border: `1.5px solid ${search ? T.primary : T.border}`,
            bgcolor: "#F8FAFC", transition: "border-color 0.15s",
          }}
        >
          <FiSearch size={13} color={search ? T.primary : T.textMuted} style={{ flexShrink: 0 }} />
          <InputBase
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by task name or no…"
            sx={{ flex: 1, fontSize: "12px", color: T.text, "& input": { p: 0 } }}
          />
          {search && (
            <IconButton size="small" onClick={handleSearchClear} sx={{ p: 0.2, color: T.textMuted }}>
              <FiX size={12} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: 200 }}>
            <CircularProgress size={28} sx={{ color: T.primary }} />
          </Stack>
        ) : tasks.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ height: 220 }}>
            <FiInbox size={40} color={T.textMuted} />
            <Typography sx={{ fontSize: "14px", color: T.textMuted, fontWeight: 600 }}>
              {debouncedSearch ? "No results" : "All caught up!"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: T.textMuted, textAlign: "center" }}>
              {debouncedSearch
                ? `No tasks matching "${debouncedSearch}"`
                : isAdmin
                  ? "No tasks pending acknowledgment"
                  : "No tasks pending your acknowledgment"}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <AnimatePresence>
              {tasks.map((task) => (
                <TaskCard
                  key={task._id}
                  task={task}
                  onNavigate={handleNavigate}
                  isAdmin={isAdmin}
                />
              ))}
            </AnimatePresence>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default PendingAcknowledgmentList;
